/**
 * Chord symbol parsing.
 *
 * `parseChordSymbol` is total: it never throws and never returns a partially-built
 * chord. Either you get a `ChordSpec` or you get a `ParseError` describing where it
 * gave up. Use `isParseError` to discriminate — `ChordSpec` has no `kind` field, so
 * the union is safe.
 *
 * ## What it accepts
 *
 * Roots are read greedily, so `Cb` is C flat, not C with a flat five. Write `C(b5)`
 * or `C7b5` if you mean the alteration. Accidentals may be ASCII (`b`, `#`, `bb`,
 * `##`, `x`) or Unicode (`♭`, `♯`, `𝄫`, `𝄪`, `♮`).
 *
 * Quality may be written as `m` / `min` / `-`, `maj` / `M` / `Δ` / `^`, `dim` / `°`,
 * `aug` / `+`, `ø` (half-diminished, folded to `m7♭5`). Case is normalised for the
 * word forms, so `CMaj7`, `Cmaj7` and `CMAJ7` all parse.
 *
 * A bare numeral means the full stack below it: `C9` is a dominant seventh plus a
 * ninth, `C11` adds the eleventh, `C13` is 7-9-13 (no eleventh, by convention).
 * `C7(add13)` is how you say "seventh plus thirteenth but no ninth", and that is
 * exactly what `formatChord` will give you back.
 *
 * ## What it deliberately rejects
 *
 * `alt` (as in C7alt) has no single agreed spelling, and guessing would put invented
 * pitch classes into the matching key. Write the alterations you actually mean.
 */

import {
  normalizeAccidentalGlyphs,
  parseNoteName,
  spelledNoteToPc,
  type KeyContext,
} from './enharmonic.js';
import {
  ALTERATION_ORDER,
  EXTENSION_DEGREES,
  EXTENSION_ORDER,
  type Alteration,
  type ChordSpec,
  type Extension,
  type SeventhType,
  type SpelledNote,
  type TriadQuality,
} from './types.js';

/** Returned instead of a `ChordSpec` when a symbol cannot be understood. */
export interface ParseError {
  readonly kind: 'parse-error';
  /** Human-readable, safe to show in the correction UI. */
  message: string;
  /** The original input, untouched. */
  input: string;
  /** The input after glyph and case normalisation — `position` indexes into this. */
  normalized: string;
  /** Index into `normalized` where parsing stopped. */
  position: number;
}

/** Discriminates the `parseChordSymbol` return union. */
export function isParseError(value: ChordSpec | ParseError): value is ParseError {
  return (value as ParseError).kind === 'parse-error';
}

function fail(input: string, normalized: string, position: number, message: string): ParseError {
  return { kind: 'parse-error', message, input, normalized, position };
}

/**
 * Fold the many ways people write the same modifier down to one lower-case spelling.
 * `+` is left alone on purpose: it means "augmented" before a numeral (`C+7`) and
 * "raised" after one (`C7+9`), so it can only be resolved positionally by the scanner.
 */
function normalizeSymbol(raw: string): string {
  let text = raw.trim().replace(/\s+/g, '');
  text = normalizeAccidentalGlyphs(text);
  text = text
    .replace(/maj/gi, 'maj')
    .replace(/min/gi, 'min')
    .replace(/dim/gi, 'dim')
    .replace(/aug/gi, 'aug')
    .replace(/sus/gi, 'sus')
    .replace(/add/gi, 'add')
    .replace(/omit/gi, 'omit')
    .replace(/no(?=[35])/gi, 'no')
    .replace(/alt/gi, 'alt');
  // Triangle and caret both mean a major seventh; bare, they imply the 7.
  text = text.replace(/[Δ△^](?=\d)/g, 'maj').replace(/[Δ△^]/g, 'maj7');
  // Degree sign is a diminished triad; half-diminished folds to m7b5 and is then
  // re-collapsed to `diminished` + `min7` by the post-pass below.
  text = text.replace(/°/g, 'dim').replace(/[øØ]7?/g, 'm7b5');
  // `M` that survived the `maj` pass is a lone major marker (CM, CM7, CM9).
  text = text.replace(/M/g, 'maj');
  // Dash variants used for minor.
  text = text.replace(/[−–—]/g, '-');
  // 6/9 must be collapsed before the slash-bass split or the "9" looks like a bass note.
  text = text.replace(/6\/9/g, '69');
  return text;
}

type Token =
  | 'maj13'
  | 'maj11'
  | 'maj9'
  | 'maj7'
  | 'add13'
  | 'add11'
  | 'add9'
  | 'add6'
  | 'add4'
  | 'add2'
  | 'omit3'
  | 'omit5'
  | 'no3'
  | 'no5'
  | 'sus2'
  | 'sus4'
  | 'sus'
  | 'dim7'
  | 'dim'
  | 'aug'
  | 'min'
  | 'maj'
  | '#11'
  | '+11'
  | 'b13'
  | 'b9'
  | '#9'
  | '+9'
  | 'b5'
  | '#5'
  | '+5'
  | '69'
  | '13'
  | '11'
  | '9'
  | '7'
  | '6'
  | '5'
  | 'm'
  | '-'
  | '+'
  | 'alt';

/**
 * Longest-match-first. Order matters in three places: the `maj*` and `min` forms must
 * precede the bare `m`, `dim7` must precede `dim`, and every two-character numeral
 * (`69`, `13`, `11`) must precede its one-character prefix.
 */
const TOKENS: readonly Token[] = [
  'maj13',
  'maj11',
  'maj9',
  'maj7',
  'add13',
  'add11',
  'add9',
  'add6',
  'add4',
  'add2',
  'omit3',
  'omit5',
  'no3',
  'no5',
  'sus2',
  'sus4',
  'sus',
  'dim7',
  'dim',
  'aug',
  'min',
  'maj',
  'alt',
  '#11',
  '+11',
  'b13',
  'b9',
  '#9',
  '+9',
  'b5',
  '#5',
  '+5',
  '69',
  '13',
  '11',
  '9',
  '7',
  '6',
  '5',
  'm',
  '-',
  '+',
];

/** Characters that carry no meaning and may appear anywhere in the modifier run. */
const SKIPPABLE = new Set(['(', ')', '[', ']', ',', ' ', '.']);

interface ScanState {
  quality: TriadQuality | null;
  seventh: SeventhType | undefined;
  extensions: Set<Extension>;
  alterations: Set<Alteration>;
  omissions: Set<'3' | '5'>;
  touched: boolean;
}

/** The seventh a bare `7` implies, which depends on the triad it sits on. */
function impliedSeventh(quality: TriadQuality | null): SeventhType {
  return quality === 'diminished' ? 'dim7' : 'min7';
}

function setQuality(state: ScanState, quality: TriadQuality): boolean {
  if (state.quality !== null && state.quality !== quality) return false;
  state.quality = quality;
  return true;
}

/** sus may override the implicit major triad but not an explicit minor/dim/aug. */
function setSus(state: ScanState, quality: 'sus2' | 'sus4'): boolean {
  if (state.quality !== null && state.quality !== 'major' && state.quality !== quality) {
    return false;
  }
  state.quality = quality;
  return true;
}

function applyToken(token: Token, state: ScanState): string | null {
  state.touched = true;
  switch (token) {
    case 'maj13':
      state.seventh = 'maj7';
      state.extensions.add('9').add('13');
      return null;
    case 'maj11':
      state.seventh = 'maj7';
      state.extensions.add('9').add('11');
      return null;
    case 'maj9':
      state.seventh = 'maj7';
      state.extensions.add('9');
      return null;
    case 'maj7':
      state.seventh = 'maj7';
      return null;
    case 'maj':
      return setQuality(state, 'major') ? null : 'conflicting chord qualities';
    case 'min':
    case 'm':
    case '-':
      return setQuality(state, 'minor') ? null : 'conflicting chord qualities';
    case 'dim7':
      // On its own, `dim7` names the whole chord. After an explicit quality it can only
      // mean "…with a diminished seventh", which is how `Cmaj(dim7)` and `C+(dim7)` —
      // the formatter's output for those otherwise unwritable chords — round-trip.
      if (state.quality !== null && state.quality !== 'diminished') {
        state.seventh = 'dim7';
        return null;
      }
      state.quality = 'diminished';
      state.seventh = 'dim7';
      return null;
    case 'dim':
      return setQuality(state, 'diminished') ? null : 'conflicting chord qualities';
    case 'aug':
    case '+':
      return setQuality(state, 'augmented') ? null : 'conflicting chord qualities';
    case 'sus2':
      return setSus(state, 'sus2') ? null : 'sus cannot be combined with that quality';
    case 'sus4':
    case 'sus':
      return setSus(state, 'sus4') ? null : 'sus cannot be combined with that quality';
    case 'add13':
      state.extensions.add('13');
      return null;
    case 'add11':
    case 'add4':
      state.extensions.add('11');
      return null;
    case 'add9':
    case 'add2':
      state.extensions.add('9');
      return null;
    case 'add6':
      state.extensions.add('6');
      return null;
    case '69':
      state.extensions.add('6').add('9');
      return null;
    case '13':
      state.seventh ??= impliedSeventh(state.quality);
      state.extensions.add('9').add('13');
      return null;
    case '11':
      state.seventh ??= impliedSeventh(state.quality);
      state.extensions.add('9').add('11');
      return null;
    case '9':
      state.seventh ??= impliedSeventh(state.quality);
      state.extensions.add('9');
      return null;
    case '7':
      state.seventh ??= impliedSeventh(state.quality);
      return null;
    case '6':
      state.extensions.add('6');
      return null;
    case '5':
      if (state.quality !== null || state.seventh || state.extensions.size > 0) {
        return 'a bare 5 only makes sense as a power chord';
      }
      state.quality = 'power';
      return null;
    case 'b9':
      state.extensions.add('b9');
      return null;
    case '#9':
    case '+9':
      state.extensions.add('#9');
      return null;
    case '#11':
    case '+11':
      state.extensions.add('#11');
      return null;
    case 'b13':
      state.extensions.add('b13');
      return null;
    case 'b5':
      state.alterations.add('b5');
      return null;
    case '#5':
    case '+5':
      state.alterations.add('#5');
      return null;
    case 'no3':
    case 'omit3':
      state.omissions.add('3');
      return null;
    case 'no5':
    case 'omit5':
      state.omissions.add('5');
      return null;
    case 'alt':
      return "'alt' is ambiguous — write the alterations explicitly, e.g. C7(#5,#9)";
  }
}

/**
 * Collapse the redundant representations so that two chords which sound identical and
 * mean identical things also compare identical:
 *  - minor + ♭5 IS a diminished triad,
 *  - major + ♯5 IS an augmented triad,
 *  - an altered ninth/eleventh/thirteenth replaces its natural counterpart.
 */
function normalizeSpecParts(state: ScanState): {
  quality: TriadQuality;
  extensions: Extension[];
  alterations: Alteration[];
  omissions: Array<'3' | '5'>;
} {
  let quality: TriadQuality = state.quality ?? 'major';
  const alterations = new Set(state.alterations);
  const extensions = new Set(state.extensions);

  if (quality === 'minor' && alterations.has('b5')) {
    quality = 'diminished';
    alterations.delete('b5');
  }
  if (quality === 'major' && alterations.has('#5')) {
    quality = 'augmented';
    alterations.delete('#5');
  }
  if (quality === 'diminished' && alterations.has('b5')) alterations.delete('b5');
  if (quality === 'augmented' && alterations.has('#5')) alterations.delete('#5');

  if (extensions.has('b9') || extensions.has('#9')) extensions.delete('9');
  if (extensions.has('#11')) extensions.delete('11');
  if (extensions.has('b13')) extensions.delete('13');

  return {
    quality,
    extensions: sortExtensions([...extensions]),
    alterations: [...alterations].sort(
      (a, b) => ALTERATION_ORDER.indexOf(a) - ALTERATION_ORDER.indexOf(b),
    ),
    omissions: [...state.omissions].sort(),
  };
}

/** Canonical extension ordering, so structurally equal chords are also deep-equal. */
export function sortExtensions(extensions: readonly Extension[]): Extension[] {
  return [...new Set(extensions)].sort(
    (a, b) => EXTENSION_ORDER.indexOf(a) - EXTENSION_ORDER.indexOf(b),
  );
}

/** True when `extensions` contains any spelling of the ninth. */
export function hasNinthDegree(extensions: readonly Extension[]): boolean {
  return extensions.some((extension) => EXTENSION_DEGREES[extension] === 9);
}

/**
 * Parse a chord symbol into a structural `ChordSpec`.
 *
 * The result carries no pitch classes; run it through `toCanonical` for those.
 */
export function parseChordSymbol(input: string): ChordSpec | ParseError {
  const normalized = normalizeSymbol(input);
  if (normalized.length === 0) {
    return fail(input, normalized, 0, 'empty chord symbol');
  }

  // --- root -----------------------------------------------------------------
  const rootMatch = /^([A-Ga-g])(bb|b|##|#|x|)/.exec(normalized);
  if (!rootMatch) {
    return fail(input, normalized, 0, 'a chord symbol must start with a note name A–G');
  }
  const root = parseNoteName(rootMatch[0]);
  if (!root) {
    return fail(input, normalized, 0, `unreadable root note "${rootMatch[0]}"`);
  }
  let cursor = rootMatch[0].length;

  // --- slash bass -----------------------------------------------------------
  let bass: SpelledNote | undefined;
  const rest = normalized.slice(cursor);
  const slashIndex = rest.lastIndexOf('/');
  let modifiers = rest;
  if (slashIndex >= 0) {
    const bassText = rest.slice(slashIndex + 1);
    const parsedBass = parseNoteName(bassText);
    if (!parsedBass) {
      return fail(
        input,
        normalized,
        cursor + slashIndex + 1,
        `"${bassText}" is not a bass note — a slash must be followed by a note name`,
      );
    }
    bass = parsedBass;
    modifiers = rest.slice(0, slashIndex);
  }

  // --- modifiers ------------------------------------------------------------
  const state: ScanState = {
    quality: null,
    seventh: undefined,
    extensions: new Set(),
    alterations: new Set(),
    omissions: new Set(),
    touched: false,
  };

  let i = 0;
  while (i < modifiers.length) {
    const char = modifiers[i] as string;
    if (SKIPPABLE.has(char)) {
      i += 1;
      continue;
    }
    const token = TOKENS.find((candidate) => modifiers.startsWith(candidate, i));
    if (!token) {
      return fail(
        input,
        normalized,
        cursor + i,
        `unrecognised chord modifier at "${modifiers.slice(i)}"`,
      );
    }
    const error = applyToken(token, state);
    if (error) {
      return fail(input, normalized, cursor + i, error);
    }
    i += token.length;
  }
  cursor += modifiers.length;

  const { quality, extensions, alterations, omissions } = normalizeSpecParts(state);

  const spec: ChordSpec = { root, quality, extensions, alterations, omissions };
  if (state.seventh) spec.seventh = state.seventh;
  // "present only when != root" — a redundant C/C is not a slash chord.
  if (bass && spelledNoteToPc(bass) !== spelledNoteToPc(root)) spec.bass = bass;

  return spec;
}

/**
 * Convenience for the correction UI and for tests: parse, and if the symbol is
 * unreadable return `null` rather than an error object.
 */
export function tryParseChordSymbol(input: string): ChordSpec | null {
  const result = parseChordSymbol(input);
  return isParseError(result) ? null : result;
}

/** Re-exported so `parse.ts` consumers do not also have to import from `enharmonic.ts`. */
export type { KeyContext };
